import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { Link as RouterLink } from 'react-router-dom'

import {
  DIRECT_INSTRUMENT_STATUS_PILL,
  mapApiStatusToUiStatus,
} from '@/features/campaigns/lib/quick-instrument-display'
import { directInstrumentOpenPath } from '@/features/instruments/lib'
import { SortableTableHeaderLabel } from '@/shared/table'
import {
  sensemakerTableBodyCellInteractiveSx,
  sensemakerTableSx,
} from '@/shared/table/sensemaker-table-theme'

import { formatAdminDashboardCreatedDate } from '../lib/format-admin-dashboard-date'
import {
  RECENT_CAMPAIGNS_TABLE,
  recentCampaignsActionsBodyCellSx,
  recentCampaignsActionsHeaderCellSx,
  recentCampaignsBodyCellSx,
  recentCampaignsHeaderCellSx,
} from '../lib/recent-campaigns-table-theme'
import { DashboardDirectInstrumentRowActions } from './DashboardDirectInstrumentRowActions'

export interface DashboardDirectInstrumentRow {
  id: string
  name: string
  apiStatus: string
  createdAt: string
}

interface Props {
  rows: DashboardDirectInstrumentRow[]
  nameSortDirection: 'asc' | 'desc' | null
  onToggleNameSort: () => void
}

export function DashboardDirectInstrumentsTable({
  rows,
  nameSortDirection,
  onToggleNameSort,
}: Readonly<Props>) {
  return (
    <TableContainer sx={{ bgcolor: RECENT_CAMPAIGNS_TABLE.bodyBg }}>
      <Table
        size="small"
        data-testid="dashboard-direct-instruments-table"
        sx={sensemakerTableSx}
      >
        <colgroup>
          <col style={{ width: RECENT_CAMPAIGNS_TABLE.colNameWidth }} />
          <col style={{ width: RECENT_CAMPAIGNS_TABLE.colStatusWidth }} />
          <col style={{ width: RECENT_CAMPAIGNS_TABLE.colDateWidth }} />
          <col style={{ width: RECENT_CAMPAIGNS_TABLE.colActionsWidth }} />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell sx={recentCampaignsHeaderCellSx}>
              <SortableTableHeaderLabel
                label="Instrument Name"
                sortDirection={nameSortDirection}
                testId="dashboard-direct-instruments-name-sort"
                onToggle={onToggleNameSort}
              />
            </TableCell>
            <TableCell sx={recentCampaignsHeaderCellSx}>Status</TableCell>
            <TableCell sx={recentCampaignsHeaderCellSx}>Created date</TableCell>
            <TableCell sx={recentCampaignsActionsHeaderCellSx}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const uiStatus = mapApiStatusToUiStatus(row.apiStatus)
            const pill = DIRECT_INSTRUMENT_STATUS_PILL[uiStatus]
            const href = directInstrumentOpenPath(row.id, row.apiStatus)

            return (
              <TableRow
                key={row.id}
                hover={false}
                data-testid={`dashboard-direct-instrument-row-${row.id}`}
                sx={{ bgcolor: RECENT_CAMPAIGNS_TABLE.bodyBg }}
              >
                <TableCell sx={recentCampaignsBodyCellSx}>
                  <Tooltip title={row.name} placement="top-start">
                    <Link
                      component={RouterLink}
                      to={href}
                      underline="hover"
                      data-testid={`dashboard-direct-instrument-link-${row.id}`}
                      sx={{
                        fontSize: '14px',
                        fontWeight: 700,
                        lineHeight: '20px',
                        color: RECENT_CAMPAIGNS_TABLE.nameColor,
                        display: 'inline-block',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        verticalAlign: 'middle',
                        '&:hover': { color: '#008cd0' },
                      }}
                    >
                      {row.name}
                    </Link>
                  </Tooltip>
                </TableCell>
                <TableCell sx={sensemakerTableBodyCellInteractiveSx}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      height: 26,
                      px: '12px',
                      py: '4px',
                      borderRadius: 9999,
                      border: `1px solid ${pill.border}`,
                      bgcolor: pill.bg,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 500,
                        lineHeight: '16px',
                        color: pill.color,
                      }}
                    >
                      {pill.label}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    ...recentCampaignsBodyCellSx,
                    color: RECENT_CAMPAIGNS_TABLE.dateColor,
                    fontSize: '14px',
                    lineHeight: '20px',
                    letterSpacing: '-0.1504px',
                  }}
                >
                  {formatAdminDashboardCreatedDate(row.createdAt)}
                </TableCell>
                <TableCell sx={recentCampaignsActionsBodyCellSx}>
                  <DashboardDirectInstrumentRowActions
                    instrumentId={row.id}
                    apiStatus={row.apiStatus}
                  />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
